<template>
  <template v-if="toolboxGroup">
    <IconClickTap size="24" style="padding:10px;" />
    <p>Button</p>
  </template>
  <template v-else>
    <div :data-element-id="elementId" :style="elementStyle" class="set">
      <Button type="button"
              :label="label"
              :icon="elementButtonIcon ? elementButtonIconImg : ''"
              :loading="loading"
              @click="handleButtonClick"
              :disabled="offline"
      />
    </div>
    <Idot :element-data="elementData"/>

    <Dialog v-model:visible="showOfflineDialog" header="Notice" modal>
      <p>You’re currently offline. Once you're back online, the save buttons will automatically re-enable.</p>
      <template #footer>
        <Button label="OK" @click="showOfflineDialog = false" />
      </template>
    </Dialog>
  </template>
</template>

<script setup lang="ts">
import {computed, toRefs, getCurrentInstance, ref, watch} from 'vue'
import {getValuePropositionAPI, ValuePropositionAPI} from "@/services/value-proposition-api";
import {useMicroservice} from "@/hooks";
import {useAuthStore, useMainStore, useSlidesStore} from "@/store";
import {storeToRefs} from "pinia";
import Idot from "@/components/Idot.vue";

const props = defineProps({
  elementData: {
    type: Object
  },
  viewer: {
    type: Boolean,
    default: false
  },
  style: Object,
  class: String
})

const authStore = useAuthStore()
const { offline } = storeToRefs(authStore)
const { theme } = storeToRefs(useSlidesStore())
const { calculatorId } = storeToRefs(useMainStore())
const { getModified } = useMicroservice()

const showOfflineDialog = ref(false)
const loading = ref(false)

const { elementData } = toRefs(props)

const elementId = computed(() => elementData.value.id ?? null)
const elementStyle = computed(() => elementData.value?.style ?? {})
const elementButtonIcon = computed(() => elementData.value?.buttonIcon ?? true)
const elementButtonColor = computed(() => elementData.value?.buttonColor ?? '#2196F3')
const elementButtonType = computed(() => elementData.value?.buttonType ?? null)
const justifyContent = computed(() => elementData.value?.style.justifyContent ?? '')
const tileAlign = computed(() => elementData.value?.style.align ?? 'center')
const textColor = computed(() => elementData.value?.style.color ?? '#fff')
const fontSize = computed(() => elementData.value?.style.fontSize ?? '20px')
const borderRadius = computed(() => px(elementData.value?.style?.borderRadius, 'none'))
const width = computed(() => px(elementData.value?.style?.containerWidth, 'auto'))
const height = computed(() =>  px(elementData.value?.style?.containerHeight, 'auto'))
const elementButtonIconImg = computed(() => elementData.value.buttonIconImg ?? null)
const generatePdf = computed(() => elementData.value.generatePdf ?? false)
const fontName = computed(() => `"${theme.value.fontName}"` ?? '')
const label = computed(() => elementData.value.text !== 'Select data from Value Model' ? elementData.value.text : 'Button')

const px = (input, defaultValue) => {
  if (input) {
    return `${input}px`
  }
  else {
    return defaultValue
  }
}

const toolboxGroup = computed(() => {
  const parentClassList = getCurrentInstance().parent.attrs.class;
  return parentClassList === "dragArea toolbox-group"
});

const handleButtonClick = () => {
  switch (elementButtonType.value) {
    case 'jspreadsheet-sync':
      jspreadsheetSync()
      break
    case 'save':
      saveUvp()
      break
    case 'report':
      generateReport()
      break
    case 'ai':
      chatWithAi()
      break
    case 'calculator-pdf':
      downloadPdf()
      break
    default:
      console.log('Please select a valid button type')
  }
}

const withTimeout = (promise: any, timeout: number = 2000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timed out")), timeout)
    ),
  ]);
};

const saveUvp = async () => {
  try {
    await withTimeout(getModified());
    const api: ValuePropositionAPI | null = getValuePropositionAPI();
    const uvpId = api?.getUVPId();

    if (uvpId && uvpId > '0') {
      api?.fireUIEvent("slider", { sliderId: "main-slider", action: "save_data" });
    } else {
      api?.fireUIEvent("slider", { sliderId: "main-slider", action: "save_as_data" });
    }
  } catch (error) {
    showOfflineDialog.value = true
    offline.value = true
  }
}

const generateReport = () => {
  const api: ValuePropositionAPI | null = getValuePropositionAPI();
  if (api?.isUnchanged()) {
    api?.fireUIEvent("slider", {sliderId: "main-slider", action: "save_data"})
  }

  document.dispatchEvent(new CustomEvent("lpi-save-pinia", { detail: {} }));

  const parsedSlideIndices = JSON.parse(elementData.value.buttonRevealSlideIndices)

  if (generatePdf.value) {
    document?.getElementById("vueReportExport")?.getVueInstance().createJobOneStep(parsedSlideIndices, 'pdf')
  }
  else {
    document?.getElementById("vueReportExport")?.getVueInstance().showDropdown(parsedSlideIndices)
  }

}

const jspreadsheetSync = () => {
  loading.value = true

  window.dispatchEvent(new CustomEvent('jspreadsheet-sync'));

  window.addEventListener('jspreadsheet-sync-end', () => {
    loading.value = false
  })
}

const chatWithAi = async () => {
  loading.value = true
  window['tree'].variablesByHookId['auto-var2'].notes = ''
  window['tree'].recalculate(Date.now(), [window['tree'].variablesByHookId['auto-var2']])

  const url = elementData.value.buttonAIEndpoint
  const notes = window['tree'].variablesByHookId['auto-var1'].notes

  const prompt = `Give me the benefits of Air Freight Sustainability related to ${notes}.  Limit the response to 3 points.`

  const response = await fetch(`${url}`, {
    headers: {
      'Authorization': 'Bearer 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',
      'Content-Type': 'application/json'
    },
    method: 'POST',
    body: JSON.stringify({
      "model": "gpt-4o-mini",
      "messages": [{"role": "user", "content": prompt}],
      "temperature": 0.7
    })
  })
  const result = await response.json()
  console.log(result)
  window['tree'].variablesByHookId['auto-var2'].notes = result.choices[0].message.content
  window['tree'].recalculate(Date.now(), [window['tree'].variablesByHookId['auto-var2']])
  loading.value = false
}

// Calculator Button Event
const downloadPdf = () => {
  if (calculatorId.value) {
    loading.value = true
    const token = localStorage.getItem('lpi-calc-token') ?? ''
    window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/public-vp/${calculatorId.value}/calculator-pdf-export?token=${token}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    })
        .then(response => {
          loading.value = false
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(async data => {
          await downloadFile(data['pdf-url'])
          loading.value = false
        })
        .catch(error => {
          console.error('Error:', error)
        });
  }
}

const downloadFile = async (url: string, openInNewTab: boolean = true) => {
  const response = await fetch(url)
  const blob = await response.blob()
  const blobUrl = window.URL.createObjectURL(blob)

  const newTab = window.open(url, '_blank')

  if (!newTab) {
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = 'Calculator Download'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    window.URL.revokeObjectURL(blobUrl)
  }
}

watch(offline, (newVal) => {
  if (newVal) {
    const fetchData = async () => {
      try {
        await withTimeout(getModified(), 1000)
        offline.value = false
      } catch (error) {
        setTimeout(fetchData, 2000)
      }
    }
    fetchData()
  }
})
</script>

<style scoped>
:deep(.text p) { margin: 0; }

.set {
  display: flex;
  align-items: v-bind(tileAlign);
  justify-content: v-bind(justifyContent);
}

:deep(.p-button) {
  background: v-bind(elementButtonColor);
  border: none;
  color: v-bind(textColor);
  font-size: v-bind(fontSize);
  font-family: v-bind(fontName);
  width: v-bind(width);
  max-width: 100vw;
  height: v-bind(height);
  border-radius: v-bind(borderRadius);
}

:deep(.pi) {
  font-size: v-bind(fontSize);
}
</style>
